import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CalendarCheck, Clock, ExternalLink, ShieldCheck, Sparkles } from 'lucide-react';
import { TopBar, Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { PageMeta, BreadcrumbJsonLd } from '@/components/PageMeta';
import { PageHero } from '@/components/PageHero';
import { ScrollProgressBar } from '@/components/ScrollProgressBar';
import { BOX_IMAGES } from '@/data/images';
import { API } from '@/lib/api';
import { DeciplusIframe } from '@/components/DeciplusIframe';

const EASE = [0.22, 1, 0.36, 1];

const DEFAULT_CFG = {
  embed_url: 'https://member-app.deciplus.pro/cf-saintchamond',
  planning_url: '',
  provider_name: 'Deciplus',
  enabled: true,
  height_px: 1100,
  fallback_cta_label: 'Me contacter pour réserver',
  fallback_message:
    "Notre planning en ligne arrive très bientôt. En attendant, contactez-nous directement, nous répondons sous 24h.",
};

export default function PlanningPage() {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/content/reservation-config`)
      .then((r) => setCfg({ ...DEFAULT_CFG, ...(r.data || {}) }))
      .catch(() => setCfg(DEFAULT_CFG));
  }, []);

  const planningUrl = cfg?.planning_url || cfg?.embed_url;
  const active = cfg?.enabled && planningUrl;

  return (
    <div className="min-h-screen">
      <PageMeta
        title="Planning"
        description="Planning en temps réel CrossFit Saint-Chamond. Consulte tous les créneaux de cours WOD, HYROX, Team WOD et Free Access, et réserve ta place en direct via Deciplus."
        path="/planning"
      />
      <BreadcrumbJsonLd items={[{ name: 'Accueil', url: '/' }, { name: 'Planning' }]} />
      <ScrollProgressBar />
      <TopBar />
      <Navigation />

      <PageHero
        pageKey="/planning"
        eyebrow="Horaires en direct"
        title="PLANNING EN TEMPS RÉEL"
        highlight="EN TEMPS RÉEL"
        subtitle="Retrouvez tous nos créneaux de cours et d'accès libre, mis à jour en direct. Réservez votre place en quelques clics."
        image={BOX_IMAGES.tireFlip}
        alt="CrossFit Saint-Chamond - Planning des cours en temps réel"
        breadcrumbs={[{ name: 'Accueil', href: '/' }, { name: 'Planning' }]}
      />

      {/* ─────────────────────────────── LIVE PLANNING ─────────────────────────────── */}
      <section className="relative section-padding bg-white" data-testid="planning-live-section">
        <div className="absolute inset-x-0 top-0 h-[38%] bg-gradient-to-b from-dark via-dark to-primary-dark pointer-events-none" />
        <div className="absolute inset-x-0 top-10 mesh-gradient opacity-25 h-[30%] pointer-events-none" />

        <div className="container-custom relative z-10">
          <div className="flex justify-center mb-8">
            <span
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 backdrop-blur-md border border-white/20 text-primary-glow text-xs font-bold tracking-[0.22em] uppercase"
              data-testid="planning-live-badge"
            >
              <span className="w-2 h-2 rounded-full bg-primary-glow animate-pulse" />
              Planning en direct · Deciplus
            </span>
          </div>
          <h2 className="text-center font-heading text-3xl md:text-5xl text-white leading-[1.05] mb-3">
            NOS <span className="text-gradient-primary italic font-display">CRÉNEAUX</span>, EN DIRECT.
          </h2>
          <p className="text-center text-white/65 text-sm md:text-base max-w-xl mx-auto mb-10">
            Le planning ci-dessous est synchronisé en temps réel avec notre logiciel Deciplus. Places disponibles,
            horaires et types de cours sont toujours à jour.
          </p>

          {cfg === null ? (
            <div className="h-[800px] flex items-center justify-center rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm">
              <div className="text-center">
                <div className="animate-pulse text-white/40 text-sm tracking-[0.22em] uppercase mb-2">
                  Chargement du planning
                </div>
                <div className="flex gap-1 justify-center">
                  <span className="w-2 h-2 rounded-full bg-primary-glow animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 rounded-full bg-primary-glow animate-bounce" style={{ animationDelay: '120ms' }} />
                  <span className="w-2 h-2 rounded-full bg-primary-glow animate-bounce" style={{ animationDelay: '240ms' }} />
                </div>
              </div>
            </div>
          ) : active ? (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: EASE }}
              className="relative rounded-[24px] overflow-hidden border border-white/15 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.5)] bg-white ring-1 ring-primary/10"
              data-testid="planning-iframe-wrapper"
            >
              <div className="flex items-center justify-between px-5 py-3 bg-white border-b border-border">
                <div className="flex items-center gap-2.5">
                  <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
                    <span className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
                  </div>
                  <span className="hidden sm:inline text-xs text-muted-foreground tracking-wider font-mono truncate max-w-[280px] md:max-w-md">
                    {planningUrl.replace('https://', '')}
                  </span>
                </div>
                <a
                  href={planningUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary-dark transition-colors"
                  data-testid="planning-external-link"
                >
                  Ouvrir en plein écran
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              <DeciplusIframe
                src={planningUrl}
                title={`Planning ${cfg.provider_name}`}
                defaultHeight={cfg.height_px || 1100}
                dataTestId="planning-iframe"
              />
            </motion.div>
          ) : (
            <PlanningFallback cfg={cfg} />
          )}

          {/* Reassurance strip */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-8">
            {[
              { icon: Clock, label: 'Places en temps réel', sub: 'Mise à jour instantanée' },
              { icon: ShieldCheck, label: 'Réservation sécurisée', sub: 'Via plateforme Deciplus' },
              { icon: CalendarCheck, label: 'Annulation gratuite', sub: "Jusqu'à 2h avant le cours" },
              { icon: Sparkles, label: 'Toujours à jour', sub: 'Synchronisé avec la box' },
            ].map((item, i) => (
              <div
                key={i}
                data-testid={`planning-trust-${i}`}
                className="flex items-start gap-3 p-4 rounded-xl bg-white border border-border hover:border-primary/40 hover:shadow-md transition-all"
              >
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <item.icon className="h-4.5 w-4.5 text-primary" strokeWidth={2.2} />
                </div>
                <div>
                  <div className="font-heading text-sm tracking-wide text-dark">{item.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{item.sub}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-14">
            <Link to="/reservation" data-testid="planning-reserve-cta">
              <Button
                size="lg"
                className="btn-shine bg-primary hover:bg-primary-light text-white font-heading tracking-wider px-10 py-7 rounded-xl glow-primary"
              >
                RÉSERVER UN COURS
                <CalendarCheck className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function PlanningFallback({ cfg }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: EASE }}
      className="relative rounded-2xl overflow-hidden border border-white/15 bg-white/5 backdrop-blur-md p-10 md:p-16 text-center"
      data-testid="planning-fallback"
    >
      <div className="absolute -top-20 -right-20 w-80 h-80 bg-primary/20 rounded-full blur-[120px] pointer-events-none" />
      <div className="relative z-10 max-w-2xl mx-auto">
        <span className="inline-block text-primary-glow font-semibold text-xs tracking-[0.3em] uppercase mb-4">
          ━━ Bientôt disponible
        </span>
        <h2 className="font-heading text-3xl md:text-4xl text-white leading-tight mb-5">
          Planning en ligne{' '}
          <span className="text-gradient-primary italic font-display">en cours d'activation</span>
        </h2>
        <p className="text-white/70 leading-relaxed mb-8">
          {cfg?.fallback_message || DEFAULT_CFG.fallback_message}
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link to="/contact" data-testid="planning-fallback-cta">
            <Button className="bg-primary hover:bg-primary-dark text-white font-heading text-sm tracking-wide px-7 py-6 rounded-lg">
              {cfg?.fallback_cta_label || DEFAULT_CFG.fallback_cta_label}
            </Button>
          </Link>
        </div>
      </div>
    </motion.div>
  );
}
